import { useState, useRef, useEffect } from 'react';
import { aiAPI } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { ScrollArea } from '../components/ui/scroll-area';
import { Switch } from '../components/ui/switch';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { Bot, Send, Loader2, Sparkles } from 'lucide-react';
import { cn } from '../lib/utils';

// 21 Oct 2026: Parse Offers and Insights tabs removed — Offers & Purchases is
// retired, and "Insights" depended on it too. Chat now only uses live Zoho
// PO/SO data, Target Prices and items (see GET /ai/chat on the backend).
export default function AIAssistantPage() {
  const [chatMessages, setChatMessages] = useState([]);
  const [chatInput, setChatInput] = useState('');
  const [chatLoading, setChatLoading] = useState(false);
  const [highAccuracy, setHighAccuracy] = useState(false);

  const chatEndRef = useRef(null);

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [chatMessages]);

  const handleSendChat = async () => {
    if (!chatInput.trim()) return;
    
    const userMessage = chatInput.trim();
    setChatInput('');
    setChatMessages(prev => [...prev, { role: 'user', content: userMessage }]);
    setChatLoading(true);
    
    try {
      const response = await aiAPI.chat(userMessage, highAccuracy);
      setChatMessages(prev => [...prev, { 
        role: 'assistant', 
        content: response.data.response,
        model: response.data.model_used
      }]);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to get AI response');
      setChatMessages(prev => [...prev, { 
        role: 'assistant', 
        content: 'Sorry, I encountered an error. Please try again.',
        error: true
      }]);
    } finally {
      setChatLoading(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="ai-assistant-page">
      <div>
        <h1 className="text-2xl font-bold tracking-tight flex items-center gap-2">
          <Bot className="h-6 w-6 text-primary" />
          AI Assistant
        </h1>
        <p className="text-muted-foreground">Powered by GPT-4o for intelligent procurement assistance</p>
      </div>

      <Card className="rounded-sm">
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-lg">Ask TradeIQ AI</CardTitle>
              <CardDescription>
                Ask questions about SKUs, targets and suppliers (live Zoho PO/SO data)
              </CardDescription>
            </div>
            <div className="flex items-center gap-2">
              <Label htmlFor="high-accuracy" className="text-sm">High Accuracy</Label>
              <Switch
                id="high-accuracy"
                checked={highAccuracy}
                onCheckedChange={setHighAccuracy}
                data-testid="high-accuracy-toggle"
              />
            </div>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          {/* Messages */}
          <ScrollArea className="h-[400px] px-4">
            {chatMessages.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-full text-center py-12">
                <Sparkles className="h-12 w-12 text-muted-foreground/50 mb-4" />
                <p className="text-muted-foreground">Ask me anything about your procurement data</p>
                <p className="text-sm text-muted-foreground mt-2">
                  Try: "Show SKUs above target today" or "What's the best supplier for CPUs?"
                </p>
              </div>
            ) : (
              <div className="space-y-4 py-4">
                {chatMessages.map((msg, idx) => (
                  <div
                    key={idx}
                    className={cn(
                      'ai-message p-3 rounded-sm',
                      msg.role === 'user' ? 'user' : 'assistant',
                      msg.error && 'bg-red-50 border border-red-200'
                    )}
                    data-testid={`chat-message-${idx}`}
                  >
                    <p className="text-sm whitespace-pre-wrap">{msg.content}</p>
                    {msg.model && (
                      <p className="text-xs text-muted-foreground mt-2">
                        Model: {msg.model}
                      </p>
                    )}
                  </div>
                ))}
                {chatLoading && (
                  <div className="ai-message assistant p-3 rounded-sm">
                    <Loader2 className="h-4 w-4 animate-spin" />
                  </div>
                )}
                <div ref={chatEndRef} />
              </div>
            )}
          </ScrollArea>
          
          {/* Input */}
          <div className="p-4 border-t">
            <div className="flex gap-2">
              <Input
                value={chatInput}
                onChange={(e) => setChatInput(e.target.value)}
                placeholder="Ask a question..."
                className="rounded-sm"
                onKeyDown={(e) => e.key === 'Enter' && !e.shiftKey && handleSendChat()}
                disabled={chatLoading}
                data-testid="chat-input"
              />
              <Button
                onClick={handleSendChat}
                disabled={chatLoading || !chatInput.trim()}
                className="rounded-sm"
                data-testid="chat-send"
              >
                {chatLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
